import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaTrash, FaPlus, FaMinus, FaShoppingBag } from 'react-icons/fa';
import { useCart } from '../../contexts/CartContext';
import analyticsService from '../../services/analyticsService';

export default function CartPage() {
  const {
    items,
    updateQuantity,
    removeFromCart,
    clearCart,
    getTotalPrice,
    getTotalSavings,
    getTotalItems,
  } = useCart();

  const handleIncreaseQuantity = (id, currentQuantity) => {
    updateQuantity(id, currentQuantity + 1);
  };

  const handleDecreaseQuantity = (id, currentQuantity) => {
    if (currentQuantity > 1) {
      updateQuantity(id, currentQuantity - 1);
    } else {
      removeFromCart(id);
    }
  };

  const totalPrice = getTotalPrice();
  const totalSavings = getTotalSavings();
  const totalItems = getTotalItems();

  useEffect(() => {
    analyticsService.trackCartView({ items, total: totalPrice });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (items.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8 min-h-screen">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center mb-8">
            <Link
              to="/productos"
              className="flex items-center text-df-muted hover:text-df-text mr-4"
            >
              <FaArrowLeft className="mr-2" />
              Volver a productos
            </Link>
            <h1 className="text-3xl font-bold text-df-text">Carrito de Compras</h1>
          </div>

          <div className="bg-df-surface border border-df-border rounded-lg shadow-md p-8 text-center">
            <div className="text-6xl mb-6 text-df-muted">
              <FaShoppingBag className="mx-auto" />
            </div>
            <h2 className="text-2xl font-semibold text-df-text mb-4">Tu carrito está vacío</h2>
            <p className="text-df-muted mb-8">
              ¡Explora nuestros increíbles productos y agrega algunos a tu carrito!
            </p>
            <Link
              to="/productos"
              className="bg-df-accent-strong hover:bg-df-accent-deep text-white px-8 py-3 rounded-lg font-semibold transition-colors inline-flex items-center"
            >
              <FaShoppingBag className="mr-2" />
              Explorar Productos
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 min-h-screen">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center mb-8">
          <Link
            to="/productos"
            className="flex items-center text-df-muted hover:text-df-text mr-4"
          >
            <FaArrowLeft className="mr-2" />
            Seguir comprando
          </Link>
          <h1 className="text-3xl font-bold text-df-text">
            Carrito de Compras
            <span className="ml-3 bg-df-accent/15 text-df-accent-hover text-lg px-3 py-1 rounded-full">
              {totalItems} item{totalItems !== 1 ? 's' : ''}
            </span>
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2">
            <div className="bg-df-surface border border-df-border rounded-lg shadow-md overflow-hidden">
              <div className="p-6 border-b border-df-border">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold">Productos en tu carrito</h2>
                  <button
                    onClick={clearCart}
                    className="text-df-danger hover:opacity-80 text-sm font-medium flex items-center"
                  >
                    <FaTrash className="mr-1" />
                    Vaciar carrito
                  </button>
                </div>
              </div>

              <div className="divide-y divide-df-border">
                {items.map(item => (
                  <div key={item.id} className="p-6">
                    <div className="flex gap-4">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-24 h-24 object-contain rounded-lg border border-df-border"
                      />

                      <div className="flex-1">
                        <h3 className="font-semibold text-lg mb-2">{item.name}</h3>
                        {item.brand && <p className="text-df-muted text-sm mb-3">{item.brand}</p>}

                        {/* Special badges */}
                        <div className="flex gap-2 mb-3">
                          {item.isOnSale && (
                            <span className="bg-df-danger/10 text-df-danger text-xs px-2 py-1 rounded">
                              ¡Oferta!
                            </span>
                          )}
                          {item.isPreorder && (
                            <span className="bg-df-success/10 text-df-success text-xs px-2 py-1 rounded">
                              Preventa
                            </span>
                          )}
                        </div>

                        {/* Price */}
                        <div className="mb-4">
                          {item.isOnSale ? (
                            <div className="flex items-center gap-2">
                              <span className="text-df-muted line-through text-sm">
                                ${item.originalPrice.toLocaleString()}
                              </span>
                              <span className="text-df-gold font-bold text-lg">
                                ${item.price.toLocaleString()}
                              </span>
                              <span className="bg-df-danger/10 text-df-danger text-xs px-2 py-1 rounded">
                                Ahorras ${(item.originalPrice - item.price).toLocaleString()}
                              </span>
                            </div>
                          ) : (
                            <span className="text-df-gold font-bold text-lg">
                              ${item.price.toLocaleString()}
                            </span>
                          )}
                        </div>

                        <div className="flex items-center justify-between">
                          {/* Quantity Controls */}
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-df-muted">Cantidad:</span>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleDecreaseQuantity(item.id, item.quantity)}
                                className="bg-df-surface3 hover:bg-df-border text-df-text w-8 h-8 rounded flex items-center justify-center"
                              >
                                <FaMinus className="text-sm" />
                              </button>
                              <span className="bg-df-surface2 px-4 py-2 rounded border border-df-border text-df-text text-center min-w-[3rem] font-semibold">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => handleIncreaseQuantity(item.id, item.quantity)}
                                className="bg-df-accent-strong hover:bg-df-accent-deep text-white w-8 h-8 rounded flex items-center justify-center"
                              >
                                <FaPlus className="text-sm" />
                              </button>
                            </div>
                          </div>

                          {/* Remove button */}
                          <button
                            onClick={() => removeFromCart(item.id)}
                            className="text-df-danger hover:opacity-80 p-2"
                            title="Eliminar del carrito"
                          >
                            <FaTrash />
                          </button>
                        </div>

                        {/* Subtotal */}
                        <div className="mt-4 p-3 bg-df-surface2 rounded-lg">
                          <div className="text-right">
                            <span className="text-sm text-df-muted">Subtotal por producto:</span>
                            <div className="text-xl font-bold text-df-gold">
                              ${(item.price * item.quantity).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-df-surface rounded-lg shadow-lg border border-df-border p-6 sticky top-8">
              <h3 className="text-xl font-semibold mb-6 text-df-text">Resumen del pedido</h3>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between py-2 border-b border-df-border">
                  <span className="text-df-muted">
                    Subtotal ({totalItems} item{totalItems !== 1 ? 's' : ''}):
                  </span>
                  <span className="font-semibold text-df-text">
                    ${totalPrice.toLocaleString()}
                  </span>
                </div>

                {totalSavings > 0 && (
                  <div className="flex justify-between py-2 border-b border-df-border text-df-success">
                    <span>Ahorros totales:</span>
                    <span className="font-semibold">-${totalSavings.toLocaleString()}</span>
                  </div>
                )}

                <div className="flex justify-between py-2 border-b border-df-border">
                  <span className="text-df-muted">Envío:</span>
                  <span className="text-df-success font-semibold">Gratis</span>
                </div>

                <div className="flex justify-between py-3 border-t-2 border-df-border text-xl font-bold">
                  <span className="text-df-text">Total:</span>
                  <span className="text-df-gold">${totalPrice.toLocaleString()}</span>
                </div>
              </div>

              <Link
                to="/checkout"
                className="block w-full bg-df-accent-strong hover:bg-df-accent-deep text-white py-4 rounded-lg font-semibold text-lg text-center transition-colors mb-4"
                onClick={e => {
                  console.log('Navegando a checkout...');
                  // Verificar si hay algún manejador de eventos que esté deteniendo la propagación
                  if (e.defaultPrevented) {
                    console.log('La navegación fue prevenida por un manejador de eventos');
                  }
                }}
              >
                Proceder al Pago
              </Link>

              <Link
                to="/productos"
                className="block w-full bg-df-surface3 hover:bg-df-border text-df-text py-3 rounded-lg font-semibold text-center transition-colors"
              >
                Seguir Comprando
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
